import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  ScaleIcon,
  AcademicCapIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  ScaleIcon as ScaleIconSolid,
  AcademicCapIcon as AcademicCapIconSolid,
  CalculatorIcon as CalculatorIconSolid,
} from '@heroicons/react/24/solid';
import { useAuth } from '../context/AuthContext';
import { useHeaderPageTitle } from '../context/PageTitleContext';
import AppHeaderTitle from '../components/AppHeaderTitle';

const NAV = [
  { to: '/portal', label: 'Inicio', icon: HomeIcon, iconActive: HomeIconSolid, exact: true },
  { to: '/portal/estado-contable', label: 'Estado', icon: ScaleIcon, iconActive: ScaleIconSolid },
  { to: '/portal/becas', label: 'Becas', icon: AcademicCapIcon, iconActive: AcademicCapIconSolid },
  { to: '/portal/presupuestos', label: 'Presupuestos', icon: CalculatorIcon, iconActive: CalculatorIconSolid },
];

function navActive(pathname: string, item: (typeof NAV)[number]) {
  const path = pathname.replace(/\/+$/, '') || '/portal';
  return item.exact ? path === item.to : path.startsWith(item.to);
}

const PortalShell: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const pageTitle = useHeaderPageTitle();

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-scout-cream md:flex">
      <aside className="hidden md:flex md:flex-col w-56 shrink-0 bg-white border-r border-scout-green/20 sticky top-0 h-screen">
        <div className="px-4 py-4 border-b border-scout-green/10">
          <p className="text-xs uppercase tracking-wide text-slate-500">Portal de grupo</p>
          <p className="text-sm font-semibold text-scout-green-dark mt-0.5">Tesorería</p>
        </div>
        <nav className="flex-1 p-2 space-y-0.5">
          {NAV.map((item) => {
            const active = navActive(location.pathname, item);
            const Icon = active ? item.iconActive : item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${
                  active
                    ? 'bg-scout-green/10 text-scout-green font-semibold'
                    : 'text-scout-green-dark/80 hover:bg-scout-cream'
                }`}
              >
                <Icon className="h-5 w-5 shrink-0" />
                {item.label}
              </Link>
            );
          })}
        </nav>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white shadow-sm border-b border-scout-green/20 sticky top-0 z-40 safe-area-pt">
          <div className="px-4 md:px-8 py-2.5 flex items-center justify-between gap-3">
            <AppHeaderTitle pageTitle={pageTitle} homeTo="/portal" />
            <button
              type="button"
              onClick={handleLogout}
              className="text-sm font-medium text-scout-green hover:text-scout-green-dark"
            >
              Salir
            </button>
          </div>
        </header>

        <main className="px-4 md:px-8 py-4 md:py-6 pb-24 md:pb-8 w-full max-w-6xl mx-auto flex-1">
          <Outlet />
        </main>
      </div>

      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-scout-green/20 safe-area-pb">
        <div className="flex items-center justify-around h-14">
          {NAV.map((item) => {
            const active = navActive(location.pathname, item);
            const Icon = active ? item.iconActive : item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex flex-col items-center justify-center flex-1 py-2 text-xs ${
                  active ? 'text-scout-green font-medium' : 'text-scout-green-dark/70'
                }`}
              >
                <Icon className="h-6 w-6 mb-0.5" />
                {item.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default PortalShell;
